import { DeviceInfo as ExpressionDeviceInfo, ExpressionInputs } from "@careevolution/mydatahelps-cel";
import appActionHandler from "../appActionHandler";
import { getDeviceInfo } from "./participantDeviceInfo";

// The device doesn't change during a session, so resolve it once and cache it. The browser path is
// synchronous; the in-app path delegates asynchronously to the native app (see WebViewStep's
// GetDeviceInfo handling) and populates the cache when it resolves.
let cachedDevice: ExpressionDeviceInfo | undefined;
let devicePromise: Promise<void> | null = null;

/**
 * Coerces a device info object into the shape CEL expects. Non-string property values (e.g. the
 * browser's undefined Device/DeviceType) are dropped so the result matches the CEL DeviceInfo
 * contract ({ [key: string]: string }).
 */
function coerceDevice(deviceInfo: { identifier: string; platform: string; properties: object }): ExpressionDeviceInfo {
    const properties: { [key: string]: string } = {};
    for (const [key, value] of Object.entries(deviceInfo.properties ?? {})) {
        if (typeof value === "string") {
            properties[key] = value;
        }
    }
    return {
        identifier: deviceInfo.identifier,
        platform: deviceInfo.platform,
        properties
    };
}

/**
 * Kicks off resolution of the device used for CEL expression evaluation, matching how WebViewStep
 * answers GetDeviceInfo: when rendered in-app we delegate to the native app for the real device;
 * otherwise we use the current browser (same output as GetDeviceInfo). Idempotent - only the first
 * call does work. Returns a promise that resolves once the device is cached, so callers can await
 * it before evaluating device-dependent expressions (the browser path resolves synchronously).
 */
export function primeExpressionDevice(): Promise<void> {
    if (!devicePromise) {
        if (appActionHandler.isEmbedded) {
            devicePromise = appActionHandler.getDeviceInfo()
                .then(deviceInfo => { cachedDevice = coerceDevice(deviceInfo); })
                .catch(() => { devicePromise = null; }); // allow a retry on the next survey
        } else {
            cachedDevice = coerceDevice(getDeviceInfo());
            devicePromise = Promise.resolve();
        }
    }
    return devicePromise;
}

/**
 * Returns the resolved device for CEL expression evaluation, priming it on first use. In the
 * browser this is available immediately; in-app it is undefined until the native app responds to
 * the prime kicked off at survey start.
 */
export function getExpressionDevice(): ExpressionDeviceInfo | undefined {
    if (!devicePromise) {
        primeExpressionDevice();
    }
    return cachedDevice;
}

/**
 * Ensures device and currentLocale are populated on the expression inputs used to evaluate CEL
 * expressions, so expressions can always branch off these properties. Values already present on
 * the base inputs are preserved; otherwise device is resolved (browser or native app) and
 * currentLocale comes from the supplied current UI language.
 */
export function populateDeviceAndLocale(baseInputs: ExpressionInputs, currentLocale?: string): ExpressionInputs {
    return {
        ...baseInputs,
        device: baseInputs.device ?? getExpressionDevice(),
        currentLocale: baseInputs.currentLocale ?? currentLocale
    };
}
